// "En vivo en redes": el cliente transmite lo que ve su cámara a YouTube, Instagram o Facebook, de 1 a 5
// minutos, con música libre de derechos y el zócalo "En vivo desde la obra · Lisual". Lo arma el servidor
// de Lisual (ffmpeg en el Editor): el cliente sólo pega la URL y la clave de transmisión de SU cuenta.
// Nunca se piden contraseñas. Las claves viajan una vez, se guardan cifradas sólo si el cliente quiere y
// se muestran enmascaradas.
// Sólo para cámaras con vivo (Reolink con camaraUID) que estén mandando imagen: lo decide el servidor.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Youtube, Instagram, Facebook, Music, Play, Pause, Radio, Square, Eye, EyeOff, Trash2, ExternalLink, AlertTriangle, CheckCircle2, Loader2, Smartphone, Monitor } from "lucide-react";
import { getConfig, getEstado, salirEnVivo, cortar, borrarDestino, musicaUrl } from "./vivoRedes.api";

const PLATAFORMAS = {
  youtube: {
    nombre: "YouTube",
    Icono: Youtube,
    color: "text-red-600",
    fondo: "bg-red-50 border-red-200",
    url: "rtmp://a.rtmp.youtube.com/live2",
    guardable: true,
    formato: "horizontal",
    pasos: [
      <>Entrá a <b>YouTube Studio</b> y tocá <b>Crear → Emitir en vivo</b>. La primera vez YouTube tarda <b>24 h</b> en habilitarte el vivo.</>,
      <>En <b>Transmisión</b>, copiá la <b>Clave de transmisión</b> (y si querés, la URL del servidor). Para probar sin que nadie lo vea, poné la visibilidad en <b>No listado</b>.</>,
      <>Pegala acá abajo y tocá <b>Salir en vivo</b>. YouTube arranca solo cuando le llega la imagen.</>,
    ],
    link: "https://studio.youtube.com/",
    linkTexto: "Abrir YouTube Studio",
  },
  instagram: {
    nombre: "Instagram",
    Icono: Instagram,
    color: "text-pink-600",
    fondo: "bg-pink-50 border-pink-200",
    url: "",
    guardable: false,
    formato: "vertical",
    pasos: [
      <>Desde una computadora, entrá a <b>instagram.com</b> con tu cuenta <b>profesional</b> (empresa o creador), tocá <b>Crear → Video en vivo</b>.</>,
      <>Poné un título y copiá la <b>URL de transmisión</b> y la <b>Clave de transmisión</b>. Instagram da una clave <b>nueva en cada vivo</b>.</>,
      <>Pegalas acá y tocá <b>Salir en vivo</b>. Cuando veas la imagen en Instagram, tocá <b>Transmitir en vivo</b> allá (y al terminar, <b>Finalizar</b>).</>,
    ],
    link: "https://www.instagram.com/",
    linkTexto: "Abrir Instagram",
  },
  facebook: {
    nombre: "Facebook",
    Icono: Facebook,
    color: "text-blue-600",
    fondo: "bg-blue-50 border-blue-200",
    url: "rtmps://live-api-s.facebook.com:443/rtmp/",
    guardable: true,
    formato: "horizontal",
    pasos: [
      <>Entrá a <b>Live Producer</b> de Facebook (facebook.com/live/producer) con la página de tu empresa y elegí <b>Software de transmisión</b>.</>,
      <>Activá <b>Usar clave persistente</b> y copiá la <b>Clave de transmisión</b>. La URL del servidor ya está cargada acá.</>,
      <>Pegala acá y tocá <b>Salir en vivo</b>. Si Facebook no arranca solo, tocá <b>Transmitir en vivo</b> allá cuando veas la imagen.</>,
    ],
    link: "https://www.facebook.com/live/producer",
    linkTexto: "Abrir Live Producer",
  },
};
const ORDEN = ["youtube", "instagram", "facebook"];

const mmss = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, Math.round(s)) % 60).padStart(2, "0")}`;

function Paso({ n, titulo, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-xs text-white">{n}</span>
        {titulo}
      </h3>
      {children}
    </section>
  );
}

const VivoRedes = ({ userdata, empresa: empresaProp }) => {
  // la página arma el objeto en cada render: se fija por contenido para no recargar en bucle
  const empresaKey = JSON.stringify(empresaProp || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const empresa = useMemo(() => empresaProp || {}, [empresaKey]);
  const feedId = userdata?.feedID || "";
  const tieneVivo = !!String(userdata?.camaraUID || "").trim();
  const [cfg, setCfg] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCfg, setErrorCfg] = useState("");
  const [plat, setPlat] = useState("youtube");
  const [url, setUrl] = useState(PLATAFORMAS.youtube.url);
  const [clave, setClave] = useState("");
  const [verClave, setVerClave] = useState(false);
  const [guardar, setGuardar] = useState(true);
  const [savedId, setSavedId] = useState("");
  const [musica, setMusica] = useState("");
  const [minutos, setMinutos] = useState(3);
  const [logo, setLogo] = useState(true);
  const [formato, setFormato] = useState("horizontal");
  const [calidad, setCalidad] = useState("normal");
  const [sonando, setSonando] = useState("");
  const [confirmar, setConfirmar] = useState(false);
  const [estado, setEstado] = useState({ estado: "libre" });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const audio = useRef(null);
  const [ahora, setAhora] = useState(Date.now());

  const cargar = useCallback(async () => {
    if (!feedId || !tieneVivo) { setCargando(false); return; }
    setCargando(true);
    setErrorCfg("");
    try {
      const c = await getConfig(feedId, empresa);
      setCfg(c);
      setEstado(c.estado || { estado: "libre" });
      setMusica((m) => m || (c.musica[0] && c.musica[0].id) || "");
      setCalidad(c.hayAlta ? (c.calidadPorDefecto || "normal") : "normal");
    } catch (e) {
      setErrorCfg(e.message);
    } finally {
      setCargando(false);
    }
  }, [feedId, tieneVivo, empresa]);
  useEffect(() => { cargar(); }, [cargar]);

  // al cambiar de plataforma: URL por defecto, formato recomendado, primera clave guardada
  const guardadas = useMemo(() => ((cfg && cfg.destinos) || []).filter((d) => d.platform === plat), [cfg, plat]);
  useEffect(() => {
    const p = PLATAFORMAS[plat];
    setUrl(p.url);
    setClave("");
    setFormato(p.formato);
    setGuardar(p.guardable);
    setError("");
  }, [plat]);
  useEffect(() => { setSavedId(guardadas[0] ? guardadas[0].id : ""); }, [guardadas]);

  // estado en vivo: se consulta cada 2 s mientras transmite
  const activo = estado.estado === "conectando" || estado.estado === "en_vivo";
  useEffect(() => {
    if (!activo) return undefined;
    const t = setInterval(async () => {
      try { setEstado({ ...(await getEstado(feedId, empresa)), _t: Date.now() }); } catch (e) { /* reintenta */ }
    }, 2000);
    const r = setInterval(() => setAhora(Date.now()), 500);
    return () => { clearInterval(t); clearInterval(r); };
  }, [activo, feedId, empresa]);

  useEffect(() => () => { if (audio.current) audio.current.pause(); }, []);
  const escuchar = (m) => {
    if (!audio.current) audio.current = new Audio();
    const a = audio.current;
    if (sonando === m.id) { a.pause(); setSonando(""); return; }
    a.src = musicaUrl(m.url);
    a.currentTime = 0;
    a.volume = 0.7;
    a.play().then(() => setSonando(m.id)).catch(() => setSonando(""));
    a.onended = () => setSonando("");
  };

  const listo = savedId ? true : (url.trim().length > 8 && clave.trim().length >= 4);
  const relay = cfg && cfg.relay;
  // MB que manda LA CÁMARA (lo que consume el 4G) según la calidad
  const mbMin = (q) => (cfg ? (typeof cfg.mbPorMinuto === "object" ? cfg.mbPorMinuto[q] || cfg.mbPorMinuto.normal : cfg.mbPorMinuto) : 0);
  const mb = Math.round(mbMin(calidad) * minutos);
  const logoEmpresa = (empresa && empresa.logo) || "";

  const arrancar = async () => {
    setEnviando(true);
    setError("");
    if (audio.current) { audio.current.pause(); setSonando(""); }
    try {
      // zócalo del cliente (sin marca Lisual): empresa, logo y coordenadas para el clima. El servidor prefiere sus propios datos.
      const body = { platform: plat, minutes: minutos, musica, formato, calidad, logo: !!(logo && logoEmpresa), logoUrl: logo ? logoEmpresa : "", empresa: (empresa && empresa.customerName) || "", coords: userdata?.coordinates || null, ...(cfg && cfg.modoAdmin ? { confirmarAdmin: true } : {}) };
      if (savedId) body.savedId = savedId;
      else Object.assign(body, { url: url.trim(), key: clave.trim(), guardar: PLATAFORMAS[plat].guardable && guardar });
      const r = await salirEnVivo(feedId, empresa, body);
      setEstado({ ...r, _t: Date.now() });
      setClave("");
      setConfirmar(false);
      if (body.guardar) cargar();
    } catch (e) {
      setError(e.message);
      setConfirmar(false);
    } finally {
      setEnviando(false);
    }
  };
  const parar = async () => {
    try { await cortar(feedId, empresa); } catch (e) { setError(e.message); }
  };
  const olvidar = async (id) => {
    if (!window.confirm("¿Borrar esta clave guardada? Para volver a transmitir vas a tener que pegarla de nuevo.")) return;
    try { const r = await borrarDestino(feedId, empresa, id); setCfg((c) => ({ ...c, destinos: r.destinos })); } catch (e) { setError(e.message); }
  };

  // ---------- estados sin formulario ----------
  if (!tieneVivo) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <Radio className="mb-2 h-8 w-8 text-gray-400" />
        <p className="font-semibold text-gray-800">Esta cámara no tiene transmisión en vivo</p>
        <p className="mt-1 max-w-md text-sm text-gray-500">Transmitir a redes está disponible para las cámaras con vivo de Lisual. Si querés sumarlo a tu obra, escribinos por el asistente.</p>
      </div>
    );
  }
  if (cargando && !cfg) {
    return <div className="flex min-h-[220px] items-center justify-center text-sm text-gray-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Revisando la cámara…</div>;
  }
  if (errorCfg) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        {errorCfg} <button type="button" className="ml-2 font-semibold underline" onClick={cargar}>Reintentar</button>
      </div>
    );
  }
  if (cfg && !cfg.disponible && !activo) {
    return (
      <div className="flex min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <AlertTriangle className="mb-2 h-8 w-8 text-amber-500" />
        <p className="font-semibold text-gray-800">{cfg.motivo}</p>
        <button type="button" onClick={cargar} className="mt-3 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100">Volver a revisar</button>
      </div>
    );
  }

  // ---------- transmitiendo ----------
  if (activo) {
    const P = PLATAFORMAS[estado.platform] || PLATAFORMAS.youtube;
    const restante = estado.estado === "en_vivo" ? Math.max(0, (estado.restanteSeg || 0) - (ahora - (estado._t || ahora)) / 1000) : estado.segundos;
    const total = estado.segundos || 1;
    const hecho = estado.estado === "en_vivo" ? Math.min(1, 1 - restante / total) : 0;
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-6 text-center">
        {estado.estado === "conectando" ? (
          <>
            <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-gray-400" />
            <p className="text-lg font-semibold text-gray-900">Despertando la cámara…</p>
            <p className="mt-1 text-sm text-gray-500">En unos segundos empieza a llegar la imagen a {P.nombre}.</p>
          </>
        ) : (
          <>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-1.5 text-sm font-bold uppercase tracking-wide text-white">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" /> En vivo en {P.nombre}
            </div>
            <p className="font-mono text-5xl font-bold tabular-nums text-gray-900">{mmss(restante)}</p>
            <p className="mt-1 text-sm text-gray-500">Se corta sola al llegar a cero.</p>
            <div className="mx-auto mt-4 h-2 max-w-md overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: `${hecho * 100}%` }} />
            </div>
            {estado.platform === "instagram" && (
              <p className="mx-auto mt-3 max-w-md text-xs text-gray-600">Acordate de tocar <b>Transmitir en vivo</b> en Instagram, y <b>Finalizar</b> cuando termine.</p>
            )}
          </>
        )}
        <button type="button" onClick={parar} className="mt-5 inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-100 active:scale-95">
          <Square className="h-4 w-4 fill-current" /> Cortar transmisión
        </button>
      </div>
    );
  }

  // ---------- formulario ----------
  const P = PLATAFORMAS[plat];
  const tope = cfg && cfg.usoHoy && cfg.usoHoy.veces >= cfg.usoHoy.tope;
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-gradient-to-br from-gray-50 to-white p-3 md:p-4 border border-gray-200">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-gray-900"><Radio className="h-5 w-5 text-red-600" /> Transmití tu obra en vivo</h2>
        <p className="mt-1 text-sm text-gray-600">Hasta {cfg ? cfg.maxMinutos : 5} minutos en YouTube, Instagram o Facebook, con música, el nombre de tu empresa y de la obra, la hora y el clima. Lo arma Lisual: vos sólo pegás la clave de transmisión de <b>tu</b> cuenta (nunca te pedimos contraseñas).</p>
      </div>

      {cfg && cfg.modoAdmin && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          Estás viendo la cuenta de <b>{cfg.cliente || "un cliente"}</b> como admin: podés probar transmitiendo a <b>tu</b> canal. Al cliente no le llega nada.
        </div>
      )}
      {estado.estado === "terminado" && <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800"><CheckCircle2 className="h-4 w-4" /> La transmisión terminó. ¡Fijate cómo quedó en {(PLATAFORMAS[estado.platform] || P).nombre}!</div>}
      {estado.estado === "error" && estado.error && <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {estado.error}</div>}

      <Paso n={1} titulo="¿Dónde querés salir en vivo?">
        <div className="grid grid-cols-3 gap-2">
          {ORDEN.map((id) => {
            const X = PLATAFORMAS[id];
            const sel = plat === id;
            return (
              <button key={id} type="button" onClick={() => setPlat(id)} aria-pressed={sel}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-sm font-semibold transition active:scale-95 ${sel ? `${X.fondo} ring-2 ring-offset-1 ring-gray-900/10` : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                <X.Icono className={`h-7 w-7 ${X.color}`} />
                <span className="text-gray-800">{X.nombre}</span>
              </button>
            );
          })}
        </div>
      </Paso>

      <Paso n={2} titulo={`Conectá tu ${P.nombre}`}>
        <ol className="mb-3 space-y-1.5 text-sm text-gray-700">
          {P.pasos.map((t, i) => (
            <li key={i} className="flex gap-2"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{i + 1}</span><span>{t}</span></li>
          ))}
        </ol>
        <a href={P.link} target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">{P.linkTexto} <ExternalLink className="h-3.5 w-3.5" /></a>

        {guardadas.length > 0 && (
          <div className="mb-3 space-y-1.5">
            {guardadas.map((d) => (
              <label key={d.id} className={`flex items-center gap-2 rounded-lg border p-2 text-sm ${savedId === d.id ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}>
                <input type="radio" name="vr-dest" checked={savedId === d.id} onChange={() => setSavedId(d.id)} />
                <span className="flex-1 truncate"><b>Clave guardada</b> <span className="font-mono text-gray-500">{d.key}</span></span>
                <button type="button" onClick={() => olvidar(d.id)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Borrar esta clave"><Trash2 className="h-4 w-4" /></button>
              </label>
            ))}
            <label className={`flex items-center gap-2 rounded-lg border p-2 text-sm ${!savedId ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}>
              <input type="radio" name="vr-dest" checked={!savedId} onChange={() => setSavedId("")} /> Usar otra clave
            </label>
          </div>
        )}

        {!savedId && (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">URL del servidor
              <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={plat === "instagram" ? "rtmps://… (la que te da Instagram)" : ""} autoComplete="off" spellCheck={false}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm text-gray-900 focus:border-gray-900 focus:outline-none" />
            </label>
            <label className="block text-xs font-medium text-gray-600">Clave de transmisión
              <div className="relative mt-1">
                <input type={verClave ? "text" : "password"} value={clave} onChange={(e) => setClave(e.target.value)} placeholder="Pegala acá" autoComplete="off" spellCheck={false}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 font-mono text-sm text-gray-900 focus:border-gray-900 focus:outline-none" />
                <button type="button" onClick={() => setVerClave((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700" title={verClave ? "Ocultar" : "Mostrar"}>
                  {verClave ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            {P.guardable ? (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={guardar} onChange={(e) => setGuardar(e.target.checked)} /> {cfg && cfg.modoAdmin ? "Guardar esta clave en MI usuario de Lisual (no en la cuenta del cliente)" : "Guardar esta clave para la próxima (cifrada; la podés borrar cuando quieras)"}
              </label>
            ) : (
              <p className="text-xs text-gray-500">Instagram da una clave nueva en cada vivo: no la guardamos.</p>
            )}
          </div>
        )}
      </Paso>

      <Paso n={3} titulo="Elegí la música y la duración">
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {((cfg && cfg.musica) || []).map((m) => (
            <div key={m.id} className={`flex items-center gap-2 rounded-lg border p-2 ${musica === m.id ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}>
              <button type="button" onClick={() => escuchar(m)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white active:scale-95" title={sonando === m.id ? "Pausar" : "Escuchar"}>
                {sonando === m.id ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
              </button>
              <button type="button" onClick={() => setMusica(m.id)} className="min-w-0 flex-1 text-left">
                <span className="block truncate text-sm font-semibold text-gray-900">{m.titulo}</span>
                <span className="block truncate text-xs text-gray-500">{m.estilo}</span>
              </button>
              <input type="radio" name="vr-musica" checked={musica === m.id} onChange={() => setMusica(m.id)} aria-label={m.titulo} />
            </div>
          ))}
        </div>
        <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-500"><Music className="h-3.5 w-3.5" /> Música propia de Lisual, libre de derechos (CC0): no te van a bajar el vivo por copyright.</p>

        <div className="mt-3">
          <span className="text-xs font-medium text-gray-600">Duración</span>
          <div className="mt-1 grid grid-cols-5 gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setMinutos(n)}
                className={`rounded-lg border py-2 text-sm font-semibold active:scale-95 ${minutos === n ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>{n} min</button>
            ))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <span className="text-xs font-medium text-gray-600">Formato</span>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              <button type="button" onClick={() => setFormato("horizontal")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-medium ${formato === "horizontal" ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}><Monitor className="h-4 w-4" /> Horizontal</button>
              <button type="button" onClick={() => setFormato("vertical")} className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-medium ${formato === "vertical" ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}><Smartphone className="h-4 w-4" /> Vertical</button>
            </div>
            <p className="mt-1 text-xs text-gray-500">{plat === "instagram" ? "Instagram se ve mejor en vertical (9:16)." : "YouTube y Facebook se ven mejor en horizontal."}</p>
          </div>
          {logoEmpresa ? (
            <label className="flex items-center gap-2 self-center rounded-lg border border-gray-200 p-2 text-sm text-gray-700">
              <input type="checkbox" checked={logo} onChange={(e) => setLogo(e.target.checked)} />
              <img src={logoEmpresa} alt="" className="h-6 max-w-[80px] object-contain" /> Mostrar el logo de tu empresa
            </label>
          ) : (
            <p className="self-center text-xs text-gray-500">En pantalla: "EN VIVO", el nombre de tu empresa y de la obra, la hora y el clima.</p>
          )}
        </div>
        {cfg && cfg.hayAlta && (
          <div className="mt-3">
            <span className="text-xs font-medium text-gray-600">Calidad</span>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {[["alta", "Alta (HD 1080p)"], ["normal", "Normal"]].map(([id, nombre]) => (
                <button key={id} type="button" onClick={() => setCalidad(id)}
                  className={`rounded-lg border px-2 py-2 text-left text-sm ${calidad === id ? "border-gray-900 bg-gray-50" : "border-gray-200"}`}>
                  <span className="block font-semibold text-gray-900">{nombre}</span>
                  <span className="block text-xs text-gray-500">{relay ? `≈ ${Math.round(mbMin(id) * minutos)} MB de datos en ${minutos} min` : id === "alta" ? "La mejor imagen de la cámara" : "Más liviana"}</span>
                </button>
              ))}
            </div>
            {relay && <p className="mt-1 text-xs text-gray-500">Esta cámara usa datos móviles: por eso viene en Normal. Alta se ve mucho mejor y gasta un poco más.</p>}
          </div>
        )}
      </Paso>

      {error && <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}</div>}

      <button type="button" disabled={!listo || tope || enviando} onClick={() => setConfirmar(true)}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-3.5 text-base font-bold text-white shadow-lg transition hover:bg-red-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none">
        <Radio className="h-5 w-5" /> Salir en vivo · {minutos} min
      </button>
      <p className="text-center text-xs text-gray-500">
        {tope ? `Ya usaste las ${cfg.usoHoy.tope} transmisiones de hoy para esta cámara.` : "Se transmite a tu canal, con tu clave. Para probar, usá un vivo No listado o Privado."}
      </p>

      {confirmar && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
            <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900"><P.Icono className={`h-5 w-5 ${P.color}`} /> ¿Salimos en vivo en {P.nombre}?</h3>
            <ul className="mt-3 space-y-1 text-sm text-gray-700">
              <li>• {minutos} minuto{minutos > 1 ? "s" : ""}, calidad {calidad === "alta" ? "Alta (HD)" : "Normal"}, formato {formato}{logo && logoEmpresa ? ", con tu logo" : ""}.</li>
              <li>• Música: {((cfg && cfg.musica) || []).find((m) => m.id === musica)?.titulo || "—"}.</li>
              <li>• Se corta sola al terminar. La podés cortar antes cuando quieras.</li>
            </ul>
            {cfg && cfg.modoAdmin && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <b>Modo prueba (admin de Lisual).</b> Vas a transmitir la cámara de <b>{cfg.cliente || "este cliente"}</b> a <b>TU</b> canal (la clave que pegaste). El cliente no recibe nada y la clave queda sólo en tu usuario. ¿Seguro?
              </div>
            )}
            {relay && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Esta cámara usa <b>datos móviles (4G)</b>: transmitir {minutos} min consume unos <b>{mb} MB</b>. La cámara se despierta ahora y se vuelve a dormir al terminar.</span>
              </div>
            )}
            {plat === "instagram" && <p className="mt-3 text-xs text-gray-600">Dejá abierta la pantalla de vivo de Instagram: cuando aparezca la imagen, tocá <b>Transmitir en vivo</b> allá.</p>}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setConfirmar(false)} className="rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={arrancar} disabled={enviando} className="flex items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:bg-gray-300">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} {cfg && cfg.modoAdmin ? "Sí, a mi canal" : "Salir en vivo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VivoRedes;
